import type { Character } from '@/schemas/character';
import { dwrClassesById } from '@/data/classes';
import { findMoveByIdGlobal } from '@/data/moves';
import { LevelRow } from '@/components/molecules';
import { cn } from '@/lib/utils';

// organism — DWR Hub sidebar: timeline level-up del PG + CTA "Sali a L{N+1}".
// Pattern .levels-rail mockup (styles.css righe 1698-1775): container bordered
// + L1 fixed creazione + N livelli successivi da character.levelUpHistory.
// In PR 2 le LevelRow sono render-only (decisione B1).

interface LevelsRailProps {
  character: Character;
  /** Callback CTA "Sali di livello". Se assente o levelUp non disponibile → CTA disabled. */
  onLevelUp?: (() => void) | undefined;
  /**
   * **FI-46 PR-B / FI-46bis**: callback click su un livello passato della
   * rail per aprire l'edit retroattivo. Se assente, le rows restano
   * display-only. Invocato per ogni livello cliccato:
   *  - L1 → handler instrada a `/character/edit-l1` (builder edit-mode, FI-46bis)
   *  - L2+ → handler instrada a `/character/edit-level-up/:level` (flow edit-mode, FI-46 PR-B)
   */
  onEditLevel?: ((level: number) => void) | undefined;
  /** Tooltip da mostrare sul CTA quando disabled (es. "PG ritirato"). */
  levelUpDisabledTooltip?: string | undefined;
  className?: string;
}

export function LevelsRail({
  character,
  onLevelUp,
  onEditLevel,
  levelUpDisabledTooltip,
  className,
}: LevelsRailProps) {
  const currentLevel = character.level;
  const nextLevel = currentLevel + 1;
  const canLevelUp = !!onLevelUp;

  return (
    <div
      className={cn('bg-paper-bright border-rule border px-[0.9rem] py-[1rem]', className)}
      aria-label="Cronologia livelli del personaggio"
    >
      <div className="border-ink mb-3 flex items-baseline justify-between border-b-[1.5px] px-[0.25rem] pb-[0.6rem]">
        <span className="font-display text-ink-soft text-[0.7rem] font-bold tracking-[0.08em] uppercase">
          Cronologia
        </span>
        <span className="font-display text-[1.05rem] font-bold">
          {currentLevel}
          <span className="font-normal opacity-50"> / 10+</span>
        </span>
      </div>

      <ol className="list-none p-0">
        <li>
          {/* FI-46bis: L1 ora cliccabile → naviga al builder pre-popolato. */}
          <LevelRow
            level={1}
            label="L1 · Creazione"
            summary={summarizeL1(character)}
            onClick={onEditLevel ? () => onEditLevel(1) : undefined}
            ariaLabel="Modifica la creazione del personaggio (livello 1)"
          />
        </li>
        {character.levelUpHistory.map((record, i) => (
          <li key={`${record.levelReached}-${i}`}>
            <LevelRow
              level={record.levelReached}
              label={`L${record.levelReached}`}
              summary={summarizeLevelUp(record, character)}
              onClick={onEditLevel ? () => onEditLevel(record.levelReached) : undefined}
              ariaLabel={`Modifica le scelte del livello ${record.levelReached}`}
            />
          </li>
        ))}
        {canLevelUp ? (
          <li className="mt-2 list-none">
            <button
              type="button"
              className="border-ink-soft hover:border-ink hover:bg-paper-soft text-ink font-display flex w-full items-center gap-[0.55rem] border-[1.5px] border-dashed bg-transparent px-[0.5rem] py-[0.6rem] text-[0.8rem] tracking-[0.08em] uppercase transition"
              onClick={onLevelUp}
            >
              <span className="bg-ink text-paper-bright inline-grid h-[22px] w-[22px] place-items-center rounded-full font-black">
                +
              </span>
              <span>Sali a L{nextLevel}</span>
            </button>
          </li>
        ) : null}
        {!canLevelUp && levelUpDisabledTooltip ? (
          <li className="mt-2 list-none">
            <p className="text-ink-faint text-xs italic">{levelUpDisabledTooltip}</p>
          </li>
        ) : null}
      </ol>
    </div>
  );
}

function summarizeL1(character: Character): string {
  const classDef = dwrClassesById[character.classId];
  const className = classDef?.name ?? capitalize(character.classId);
  const raceName =
    character.race.kind === 'preset' ? capitalize(character.race.id) : character.race.name;
  const archetypeName = classDef?.archetypes.find((a) => a.id === character.archetypeId)?.name;
  return [className, raceName, archetypeName].filter(Boolean).join(' · ');
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Compone il sommario testuale del record level-up. Pattern coerente con
 * la memoria utente `feedback_user_facing_copy`: gli id schema-stile
 * (`mago.famiglio`, `elfo`) vengono resolti a nome leggibile via
 * `findMoveByIdGlobal` (mosse cross-class incluse) e `classDef.archetypes`.
 */
function summarizeLevelUp(
  record: Character['levelUpHistory'][number],
  character: Character,
): string {
  const parts: string[] = [];
  if (record.statIncreased) parts.push(`+1 ${record.statIncreased.toUpperCase()}`);
  if (record.moveAdded) {
    const moveName = findMoveByIdGlobal(record.moveAdded)?.name ?? record.moveAdded;
    parts.push(`Mossa: ${moveName}`);
  }
  if (record.spellsAdded?.length) parts.push(`Inc.: ${record.spellsAdded.length}`);
  if (record.secondaryArchetypeAdded) {
    const archetypeName = resolveArchetypeName(record.secondaryArchetypeAdded, character);
    parts.push(`Arch.: ${archetypeName}`);
  }
  return parts.join(' · ');
}

function resolveArchetypeName(archetypeId: string, character: Character): string {
  const classDef = dwrClassesById[character.classId];
  return classDef?.archetypes.find((a) => a.id === archetypeId)?.name ?? archetypeId;
}
